/**
 * DeliveryLocationList — the grouped list of campus delivery points.
 *
 * Shared by the two places a student picks a hostel: the header's "Deliver to"
 * and checkout. Both show the same campus locations, so they should look and
 * group identically — a student who learns the list in one should recognise it
 * in the other. Before this, the grouping order and the row markup existed
 * twice and had already begun to drift.
 *
 * What differs between the two callers is only BEHAVIOUR, which is why that is
 * all this takes as props: the header saves the choice to the account (and so
 * passes `busyId` to spin the row it is saving), while checkout just picks a
 * value for this one order.
 */

import { MapPin, Check } from 'lucide-react';
import { Spinner } from '@/components/ui';

/** Groups, in the order a student expects to scan them. */
const KIND_HEADING = {
  HOSTEL: 'Hostels',
  BLOCK: 'Academic blocks',
  DEPARTMENT: 'Departments',
  GATE: 'Gates',
  OTHER: 'Other',
};
const KIND_ORDER = ['HOSTEL', 'BLOCK', 'DEPARTMENT', 'GATE', 'OTHER'];

export function DeliveryLocationList({ locations, selectedId, onSelect, busyId = '' }) {
  const grouped = KIND_ORDER.map((kind) => ({
    kind,
    items: locations.filter((location) => location.kind === kind),
  })).filter((group) => group.items.length > 0);

  return (
    /**
     * Capped and scrollable. A campus with twenty hostels would otherwise push
     * the bottom sheet past the top of the screen, leaving the first group
     * unreachable on a phone.
     */
    <div className="max-h-[60vh] space-y-4 overflow-y-auto" role="listbox">
      {grouped.map((group) => (
        <div key={group.kind}>
          <p className="mb-1.5 text-xs font-bold tracking-wide text-muted uppercase">
            {KIND_HEADING[group.kind] ?? group.kind}
          </p>
          <div className="space-y-2">
            {group.items.map((location) => {
              const active = String(location._id) === String(selectedId);
              return (
                <button
                  key={location._id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  disabled={Boolean(busyId)}
                  onClick={() => onSelect(location._id)}
                  className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors disabled:opacity-60 ${
                    active ? 'border-primary-500 bg-primary-50' : 'border-line hover:bg-canvas'
                  }`}
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary-100 text-primary-600">
                    <MapPin size={18} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">
                      {location.name}
                    </span>
                    {location.description && (
                      <span className="block truncate text-xs text-muted">
                        {location.description}
                      </span>
                    )}
                  </span>
                  {busyId === location._id ? (
                    <Spinner size="sm" className="text-primary-600" />
                  ) : (
                    active && <Check size={18} className="shrink-0 text-primary-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default DeliveryLocationList;
